import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ProofImage, authedFileUrl } from "./ProofImage";
import { Check, X, BadgeCheck, ShieldAlert, Clock, ExternalLink } from "lucide-react";

const BADGE = {
  APPROVED: { cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", Icon: BadgeCheck },
  REJECTED: { cls: "border-red-500/30 bg-red-500/10 text-red-300", Icon: ShieldAlert },
  PENDING: { cls: "border-amber-500/30 bg-amber-500/10 text-amber-300", Icon: Clock },
};

export function ProofReview({ candidate, canReview, onDone }) {
  const { t } = useLang();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const status = candidate.proof_status || (candidate.membership_proof_file_id ? "PENDING" : null);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/coach-candidates/${candidate.candidate_id}/verify-proof`, { decision, note: note || null });
      toast.success(decision === "APPROVED" ? t("coach.proofApproved") : t("coach.proofRejected"));
      setNote("");
      onDone();
    } catch (e) { toast.error(extractError(e).message); }
    finally { setBusy(false); }
  };

  if (!candidate.membership_proof_file_id) {
    return (
      <p data-testid={`candidate-proof-none-${candidate.candidate_id}`} className="text-xs text-amber-300 pt-2 border-t border-white/5">
        {t("coach.proofNone")}
      </p>
    );
  }

  const badge = BADGE[status] || BADGE.PENDING;

  return (
    <div data-testid={`candidate-proof-${candidate.candidate_id}`} className="pt-3 border-t border-white/5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("coach.proofTitle")}</span>
        <span data-testid={`candidate-proof-status-${candidate.candidate_id}`}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${badge.cls}`}>
          <badge.Icon className="h-3 w-3" /> {t(`coach.proofStatus_${status}`)}
        </span>
        {candidate.proof_note && <span className="text-xs text-slate-400">— {candidate.proof_note}</span>}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="space-y-1">
          <ProofImage fileId={candidate.membership_proof_file_id} testId={`candidate-proof-img-${candidate.candidate_id}`} className="h-28 w-28" />
          <a href={authedFileUrl(candidate.membership_proof_file_id)} target="_blank" rel="noreferrer"
            data-testid={`candidate-proof-open-${candidate.candidate_id}`} className="inline-flex items-center gap-1 text-[11px] text-[#10B981] hover:underline">
            <ExternalLink className="h-3 w-3" /> {t("coach.proofView")}
          </a>
        </div>
        {canReview && (
          <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label className="text-slate-300 text-xs">{t("coach.proofNote")}</label>
              <Input data-testid={`candidate-proof-note-${candidate.candidate_id}`} value={note} onChange={(e) => setNote(e.target.value)}
                className="bg-[#1C202E] border-white/10 text-white h-10" />
            </div>
            <Button data-testid={`candidate-proof-approve-${candidate.candidate_id}`} disabled={busy} onClick={() => decide("APPROVED")}
              className="bg-emerald-600 hover:bg-emerald-500 text-white h-10"><Check className="h-4 w-4 mr-1" /> {t("coach.proofApprove")}</Button>
            <Button data-testid={`candidate-proof-reject-${candidate.candidate_id}`} disabled={busy} onClick={() => decide("REJECTED")}
              className="bg-red-600 hover:bg-red-500 text-white h-10"><X className="h-4 w-4 mr-1" /> {t("coach.proofReject")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
